import type {MemorySnapshot} from '@reados/contracts';

export interface MemorySettingsState {
 snapshot:MemorySnapshot|null;
 loading:boolean;
 operation:'global'|'reset'|null;
 error:string;
}
interface MemoryTransport {
 read:(signal:AbortSignal)=>Promise<MemorySnapshot>;
 setEnabled:(enabled:boolean,signal:AbortSignal)=>Promise<MemorySnapshot>;
 reset:()=>Promise<MemorySnapshot>;
}
/** A mixed read/generate configuration is not a confirmed global on/off state. */
export function memoryEnabled(snapshot:MemorySnapshot|null):boolean|null{
 if(!snapshot||[snapshot.enabled,snapshot.useMemories,snapshot.generateMemories].some(value=>typeof value!=='boolean'))return null;
 const read=snapshot.enabled&&snapshot.useMemories,generate=snapshot.enabled&&snapshot.generateMemories;
 return read===generate?read:null;
}
const initial=():MemorySettingsState=>({snapshot:null,loading:true,operation:null,error:''});
const message=(error:unknown)=>error instanceof Error?error.message:'记忆设置暂不可用，请重试。';

/** Owned by one mounted composer/account. No module or browser-storage cache. */
export function createMemorySettings(transport:MemoryTransport){
 let state=initial(),active=false,generation=0,request:AbortController|undefined;
 const listeners=new Set<()=>void>();
 const publish=(next:Partial<MemorySettingsState>)=>{state={...state,...next};listeners.forEach(listener=>listener());};
 const current=(id:number)=>active&&generation===id;
 async function refresh(){
  if(!active||state.operation)return;
  request?.abort();request=new AbortController();const id=++generation;
  publish({loading:true,error:''});
  try{const snapshot=await transport.read(request.signal);if(current(id))publish({snapshot,loading:false});}
  catch(error){if(current(id))publish({loading:false,error:message(error)});}
 }
 async function mutate(operation:'global'|'reset',enabled?:boolean){
  if(!active||state.loading||state.operation||!state.snapshot)return null;
  if(operation==='global'&&(state.error||memoryEnabled(state.snapshot)===null||typeof enabled!=='boolean'))return null;
  request?.abort();request=new AbortController();const id=++generation;
  publish({operation,error:''});
  try{
   const snapshot=operation==='global'?await transport.setEnabled(enabled!,request.signal):await transport.reset();
   if(!current(id))return null;
   if(operation==='global'&&(snapshot.enabled!==enabled||snapshot.useMemories!==enabled||snapshot.generateMemories!==enabled))throw new Error('服务端尚未确认记忆总开关，请刷新后重试。');
   publish({snapshot,operation:null});return snapshot;
  }catch(error){if(current(id))publish({operation:null,error:message(error)});return null;}
 }
 return {
  getSnapshot:()=>state,
  subscribe:(listener:()=>void)=>{listeners.add(listener);return()=>{listeners.delete(listener);};},
  activate:()=>{active=true;publish(initial());void refresh();},
  deactivate:()=>{active=false;++generation;request?.abort();},
  refresh,
  setEnabled:(enabled:boolean)=>mutate('global',enabled),
  reset:()=>mutate('reset'),
 };
}
export type MemorySettingsController=ReturnType<typeof createMemorySettings>;
